import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputListItem } from '../footerTaps'

import './terminal.scss'

type TerminalTapProps = {
  inputList: Array<InputListItem>
  setInputList: (value: Array<InputListItem>) => void
}

function TerminalTap(props: Readonly<TerminalTapProps>) {
  const [input, setInput] = useState('')
  const [commandHistory, setCommandHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const terminalRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLInputElement>(null)
  const { i18n } = useTranslation()

  const commandList = [
    'clear', 'help', 'echo', 'date', 'time', 'theme', 'dark', 'light', 'lang',
    'history', 'matrix', 'fortune', 'quote', 'cowsay', 'joke', 'cls'
  ]

  const jokes = [
    "Why do programmers prefer dark mode? Because light attracts bugs!",
    "Why do Java developers wear glasses? Because they don't C#!",
    "How many programmers does it take to change a light bulb? None, that's a hardware problem!",
    "A SQL query walks into a bar, walks up to two tables and asks: 'Can I join you?'",
    "!false - It's funny because it's true!"
  ]

  const quotes = [
    "Code is like humor. When you have to explain it, it's bad. - Cory House",
    "First, solve the problem. Then, write the code. - John Johnson",
    "Experience is the name everyone gives to their mistakes. - Oscar Wilde",
    "In order to be irreplaceable, one must always be different. - Coco Chanel",
    "Simplicity is the soul of efficiency. - Austin Freeman"
  ]

  useEffect(() => {
    focusInput()
    displayWelcomeMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  const displayWelcomeMessage = () => {
    const welcomeMsg = (
      <div className='welcome-message'>
        <pre className='ascii-art'>{`
██████╗ ██╗    ██╗ █████╗ ██╗███╗   ██╗
██╔══██╗██║    ██║██╔══██╗██║████╗  ██║
██║  ██║██║ █╗ ██║███████║██║██╔██╗ ██║
██║  ██║██║███╗██║██╔══██║██║██║╚██╗██║
██████╔╝╚███╔███╔╝██║  ██║██║██║ ╚████║
╚═════╝  ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝`}</pre>
        <div style={{ marginTop: '12px', color: 'var(--color-featureColor)' }}>
          Welcome to DWAIN Terminal v1.0
        </div>
        <div className='welcome-help-text'>
          Type 'help' to see available commands
        </div>
      </div>
    )
    props.setInputList([{ text: welcomeMsg, disabledPs: true }])
  }

  const addToHistory = (cmd: string) => {
    if (cmd.trim() !== '') {
      setCommandHistory(prev => [...prev, cmd])
    }
  }

  const generateTextInTerminal = (div: JSX.Element) => {
    const newInputList = [...props.inputList]
    newInputList.push({ text: input })
    newInputList.push({ text: div, disabledPs: true })
    props.setInputList(newInputList)
    addToHistory(input)
    setInput('')
    setHistoryIndex(-1)
  }

  const executeCommand = (cmd: string) => {
    const parts = cmd.trim().split(' ')
    const command = parts[0].toLowerCase()
    const args = parts.slice(1)

    switch (command) {
      case 'clear':
      case 'cls':
        props.setInputList([])
        setInput('')
        return

      case 'help':
        return renderHelp()

      case 'echo':
        return renderEcho(args.join(' '))

      case 'date':
        return renderDate()

      case 'time':
        return renderTime()

      case 'theme':
        return renderThemeToggle()

      case 'dark':
        return setTheme('dark')

      case 'light':
        return setTheme('light')

      case 'lang':
        return handleLangChange(args[0])

      case 'history':
        return renderHistory()

      case 'matrix':
        return renderMatrix()

      case 'fortune':
      case 'quote':
        return renderQuote()

      case 'cowsay':
        return renderCowsay(args.join(' '))

      case 'joke':
        return renderJoke()

      default:
        if (cmd.trim() === '') {
          const newInputList = [...props.inputList]
          newInputList.push({ text: input })
          props.setInputList(newInputList)
          setInput('')
          return
        }
        return renderInvalidCommand(command)
    }
  }

  const renderHelp = () => {
    const div = (
      <div className='inputText-text inputText-text-valid'>
        <strong>Available Commands:</strong>
        <br/><br/>
        <strong>Utilities:</strong>
        <br/>
        {`  echo <text>       `} - Display text
        <br/>
        {`  date              `} - Show current date
        <br/>
        {`  time              `} - Show current time
        <br/>
        {`  theme             `} - Toggle theme
        <br/>
        {`  dark/light        `} - Set specific theme
        <br/>
        {`  lang <de|en>      `} - Change language
        <br/>
        {`  history           `} - Command history
        <br/>
        {`  clear, cls        `} - Clear terminal
        <br/><br/>
        <strong>Fun:</strong>
        <br/>
        {`  matrix            `} - Matrix effect
        <br/>
        {`  quote, fortune    `} - Random quote
        <br/>
        {`  joke              `} - Random joke
        <br/>
        {`  cowsay <text>     `} - ASCII cow
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderEcho = (text: string) => {
    const div = (
      <div className='inputText-text inputText-text-valid'>
        {text || ''}
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderDate = () => {
    const now = new Date()
    const dateStr = now.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    const div = (
      <div className='inputText-text inputText-text-valid'>
        {dateStr}
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderTime = () => {
    const now = new Date()
    const timeStr = now.toLocaleTimeString('de-DE')
    const div = (
      <div className='inputText-text inputText-text-valid'>
        {timeStr}
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderThemeToggle = () => {
    const htmlElement = document.getElementById('theme')
    const isDark = htmlElement?.classList.contains('dark')
    const newTheme = isDark ? 'light' : 'dark'

    if (isDark) {
      htmlElement?.classList.remove('dark')
      htmlElement?.classList.add('light')
    } else {
      htmlElement?.classList.remove('light')
      htmlElement?.classList.add('dark')
    }

    const div = (
      <div className='inputText-text inputText-text-valid'>
        Theme switched to {newTheme} mode
      </div>
    )
    generateTextInTerminal(div)
  }

  const setTheme = (theme: 'dark' | 'light') => {
    const htmlElement = document.getElementById('theme')
    htmlElement?.classList.remove('dark', 'light')
    htmlElement?.classList.add(theme)

    const div = (
      <div className='inputText-text inputText-text-valid'>
        Theme set to {theme} mode
      </div>
    )
    generateTextInTerminal(div)
  }

  const handleLangChange = (lang: string) => {
    if (!lang) {
      const div = (
        <div className='inputText-text inputText-text-invalid'>
          Usage: lang {'<de|en>'}
          <br/>
          Current language: {i18n.language}
        </div>
      )
      generateTextInTerminal(div)
      return
    }

    if (lang === 'de' || lang === 'en') {
      i18n.changeLanguage(lang)
      const div = (
        <div className='inputText-text inputText-text-valid'>
          Language changed to {lang.toUpperCase()}
        </div>
      )
      generateTextInTerminal(div)
    } else {
      const div = (
        <div className='inputText-text inputText-text-invalid'>
          Invalid language. Use 'de' or 'en'
        </div>
      )
      generateTextInTerminal(div)
    }
  }

  const renderHistory = () => {
    if (commandHistory.length === 0) {
      const div = (
        <div className='inputText-text inputText-text-valid'>
          No command history yet.
        </div>
      )
      generateTextInTerminal(div)
      return
    }

    const div = (
      <div className='inputText-text inputText-text-valid'>
        <strong>Command History:</strong>
        <br/>
        {commandHistory.map((cmd, idx) => (
          <div key={idx}>{idx + 1}. {cmd}</div>
        ))}
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderMatrix = () => {
    const div = (
      <div className='inputText-text' style={{ color: '#00ff00' }}>
        <pre>{`
╔══════════════════════════════════════╗
║  Wake up, Neo...                     ║
║  The Matrix has you...               ║
║  Follow the white rabbit.            ║
║                                      ║
║  Knock, knock, Neo.                  ║
╚══════════════════════════════════════╝
        `}</pre>
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderQuote = () => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)]
    const div = (
      <div className='inputText-text inputText-text-valid'>
        {randomQuote}
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderCowsay = (text: string) => {
    const message = text || 'Moo!'
    const border = '_'.repeat(message.length + 2)
    const div = (
      <div className='inputText-text inputText-text-valid'>
        <pre>{`
 ${border}
< ${message} >
 ${'-'.repeat(message.length + 2)}
        \\   ^__^
         \\  (oo)\\_______
            (__)\\       )\\/\\
                ||----w |
                ||     ||
        `}</pre>
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderJoke = () => {
    const randomJoke = jokes[Math.floor(Math.random() * jokes.length)]
    const div = (
      <div className='inputText-text inputText-text-valid'>
        {randomJoke}
      </div>
    )
    generateTextInTerminal(div)
  }

  const renderInvalidCommand = (cmd: string) => {
    const div = (
      <div className='inputText-text inputText-text-invalid'>
        The name "{cmd}" was not recognized as the name of a cmdlet, function, or script file.
        <br/>
        Type 'help' to see available commands.
      </div>
    )
    generateTextInTerminal(div)
  }

  const _handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      executeCommand(input)
      scrollTerminal()
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1)
        setHistoryIndex(newIndex)
        setInput(commandHistory[newIndex])
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1)
          setInput('')
        } else {
          setHistoryIndex(newIndex)
          setInput(commandHistory[newIndex])
        }
      }
    }

    if (e.key === 'Tab') {
      e.preventDefault()
      if (input.trim().length > 0) {
        const matches = commandList.filter(cmd => cmd.startsWith(input.toLowerCase()))
        if (matches.length === 1) {
          setInput(matches[0])
        } else if (matches.length > 1) {
          const div = (
            <div className='inputText-text inputText-text-valid'>
              {matches.join('  ')}
            </div>
          )
          const newInputList = [...props.inputList]
          newInputList.push({ text: input })
          newInputList.push({ text: div, disabledPs: true })
          props.setInputList(newInputList)
        }
      }
    }
  }

  const scrollTerminal = () => {
    setTimeout(() => {
      if (terminalRef?.current) {
        terminalRef.current.scrollTop = terminalRef.current.scrollHeight
      }
    }, 50)
  }

  const focusInput = () => {
    textareaRef.current?.focus()
  }

  return (
    <div className='tap-terminal' ref={terminalRef} onClick={() => focusInput()} onKeyDown={_handleKeyDown}>

      {props.inputList.map((item, idx) => {
        if (item.disabledPs) {
          return (
            <div key={idx} className='terminal-input'>
              <div className='inputText noPs'>{item.text}</div>
            </div>
          )
        }
        return (
          <div key={idx} className='terminal-input'>
            <div>{'PS C:\\workspace\\dwain>'}</div>
            <div className='inputText'>{item.text}</div>
          </div>
        )
      })}

      <div className='terminal-input'>
        {'PS C:\\workspace\\dwain>'}
        <input
          className='inputText'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          type='text'
          onKeyDown={_handleKeyDown}
          ref={textareaRef}
        />
      </div>

    </div>
  )
}

export default TerminalTap
